import React, { useState, useEffect } from "react";
import Slider from "react-slick";
import styled, { keyframes } from 'styled-components';
import SampleNextArrow from "./SampleNextArrow";
import SamplePrevArrow from "./SamplePrevArrow";
import apiService from "../../../services/api_services.js";
import { useNavigate } from 'react-router-dom';







// Styled components
const Container = styled.div`
  width: 100%;
  text-align: center; /* Center the heading */
`;

const HeadingStyled = styled.h2`
  font-size: 2rem; /* Bigger font size */
  font-weight: bold; /* Bold text */
  color: #333; /* Darker color */
  margin-bottom: 20px; /* Spacing below the heading */
  position: relative; /* Allow for pseudo-element for underline */
  
  &::after {
    content: '';
    display: block;
    width: 60px; /* Width of the underline */
    height: 4px; /* Height of the underline */
    background-color: #1e81b0; /* Underline color */
    margin: 0 auto; /* Center it */
    margin-top: 5px; /* Space between heading and underline */
  }
`;

const ProductCard = styled.div`
  padding: 1rem;
  background: white;
  border: 1px solid #eaeaea;
  border-radius: 10px;
  text-align: center;
  cursor: pointer;
  margin: 0 10px; /* Space between cards */
  position: relative; /* Allow for absolute positioning */
`;

const ImageWrapper = styled.div`
  position: relative;
  height: 200px;
  border-radius: 10px;
  overflow: hidden;

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: opacity 0.5s;

    &:first-child {
      opacity: 1;
    }

    &:nth-child(2) {
      opacity: 0;
    }
  }

  &:hover img:first-child {
    opacity: 0;
  }

  &:hover img:nth-child(2) {
    opacity: 1;
  }
`;

const Title = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin-top: 1rem;
`;

const Description = styled.p`
  color: #777;
  margin: 0.5rem 0;
`;

const Price = styled.p`
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e81b0;
`;

const DiscountLabel = styled.span`
  background-color: #ff5c5c; /* Red background for discounts */
  color: white;
  padding: 0.2em 0.5em;
  border-radius: 4px;
  position: absolute;
  top: 15px;
  left: 15px;
  font-size: 0.8rem;
`;

const ColorOptions = styled.div`
  margin-top: 0.5rem;
`;

const ColorDot = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
  margin: 0 5px;
  cursor: pointer;

  &:hover {
    border: 2px solid #333; /* Optional hover effect for color choices */
  }
`;

const ShowMoreButton = styled.button`
  display: block; /* Center button */
  margin: 20px auto; /* Auto left/right margins for centering */
  padding: 10px 20px;
  border: 2px solid #1e81b0;
  border-radius: 30px;
  background: transparent;
  color: #1e81b0;
  cursor: pointer;
  transition: background 0.3s, color 0.3s;

  &:hover {
    background: #1e81b0;
    color: white;
  }
`;

// Keyframes for animation
const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(-20px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const ErrorMessage = styled.div`
  color: #ff5c5c;
  font-size: 1.5rem;
  font-weight: bold;
  padding: 1rem 2rem;
  background-color: #fbe0e0;
  border-radius: 10px;
  margin: 2rem auto;
  display: inline-block;
  animation: ${fadeIn} 0.5s ease-out;
`;

const LoadingMessage = styled.div`
  color: #1e81b0;
  font-size: 1.5rem;
  font-weight: bold;
  padding: 1rem 2rem;
  background-color: #e0f7ff;
  border-radius: 10px;
  margin: 2rem auto;
  display: inline-block;
  animation: ${fadeIn} 0.5s ease-out;
`;

const SkeletonLoader = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem 0;
  overflow: hidden;
`;

const SkeletonCard = styled.div`
  flex: 0 0 calc(20% - 1rem);
  background: #f3f3f3;
  border-radius: 10px;
  overflow: hidden;
  margin: 0 0.5rem;
  
  @media (max-width: 1200px) {
    flex: 0 0 calc(25% - 1rem);
  }
  
  @media (max-width: 992px) {
    flex: 0 0 calc(33.333% - 1rem);
  }
  
  @media (max-width: 768px) {
    flex: 0 0 calc(50% - 1rem);
  }
  
  @media (max-width: 480px) {
    flex: 0 0 calc(100% - 1rem);
  }
`;

const SkeletonImage = styled.div`
  width: 100%;
  height: 200px;
  background: #e0e0e0;
  margin-bottom: 1rem;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent);
    animation: loading 1.5s infinite;
  }
  
  @keyframes loading {
    100% {
      left: 100%;
    }
  }
`;

const SkeletonText = styled.div`
  height: 16px;
  background: #e0e0e0;
  margin: 0.5rem 1rem;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent);
    animation: loading 1.5s infinite ${props => props.delay || '0s'};
  }
`;

const BestSellers = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const allColors = {
    black: "#000",
    white: "#fff",
    brown: "#964B00",
    grey: "#808080",
    purple: "#A020F0",
    green: "#008000",
    pink: "#FFC0CB",
    blue: "#4169E1",
    red: "#FF2800",
    yellow: "#FFFF00",
  };

  const getSimilarProductIconColor = (slug) => {
    if (!slug) return "#000000";
    const colorKeys = Object.keys(allColors);
    const lowerSlug = slug.toLowerCase();
    for (const color of colorKeys) {
      if (lowerSlug.includes(color)) {
        return allColors[color];
      }
    }
    return "#000000";
  };

  const settings = {
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
    responsive: [
      { breakpoint: 1025, settings: { slidesToShow: 3, slidesToScroll: 1 } },
      { breakpoint: 769, settings: { slidesToShow: 2, slidesToScroll: 1 } },
      { breakpoint: 480, settings: { slidesToShow: 1, slidesToScroll: 1 } },
    ],
  };

  const fetchProducts = async () => {
    const filters = [{ key: "ca_id", value: 10 }];
    try {
      const data = await apiService.fetchProductsWithFilters(filters, 10, 0, "getproducts");
      setProducts(data.data.map((product) => ({ ...product, selectedProduct: product })));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleCardClick = (product) => {
    navigate(`/product/${product.pr_id}/0`, { state: { product } });
  };

  const handleColorSelect = (productIndex, selectedSlug) => {
    setProducts((prevProducts) => {
      const updatedProducts = [...prevProducts];
      const product = updatedProducts[productIndex];
      const selectedRelatedProduct = product.relatedProducts.find(
        (item) => item.slug === selectedSlug
      );
      if (selectedRelatedProduct) {
        updatedProducts[productIndex] = {
          ...product,
          selectedProduct: selectedRelatedProduct,
        };
      }
      return updatedProducts;
    });
  };

  const handleShowMoreClick = () => {
    const message = "Here is some Best Computer Glases";
    const key = 'ca_id';
    const value = 10;
    const table = 'getproducts';
    // Pass the message to ShowMoreComponent via state
    navigate('/prodList', { state: { message, key,value,table} });
  };

  if (loading) {
    return (
      <Container>
        <HeadingStyled>Best Sellers</HeadingStyled>
        <SkeletonLoader>
          {[...Array(5)].map((_, index) => (
            <SkeletonCard key={index}>
              <SkeletonImage />
              <SkeletonText style={{ width: '80%' }} />
              <SkeletonText style={{ width: '60%' }} delay="0.2s" />
              <SkeletonText style={{ width: '40%', marginBottom: '1rem' }} delay="0.4s" />
            </SkeletonCard>
          ))}
        </SkeletonLoader>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>Error in Fetching Data: {error}</ErrorMessage>
      </Container>
    );
  }

  return (
    <Container>
      <div className="px-4">
        <div className="flex items-center justify-center my-6">
          <div className="w-full border-t border-gray-300"></div>
          <span className="mx-4 text-black text-sm md:text-base font-semibold whitespace-nowrap">
            GENIE SUGGESTIONS
          </span>
          <div className="w-full border-t border-gray-300"></div>
        </div>
      </div>
      {/* <HeadingStyled>Genie Suggestions</HeadingStyled> */}
      <Slider {...settings}>
        {products.map((product, index) => {
          const { selectedProduct } = product;
          const images = JSON.parse(selectedProduct.pr_image_json || "[]");
          const firstImage = images[0] || "https://via.placeholder.com/150";
          const secondImage = images[1] || firstImage;
          	            const showDiscount = selectedProduct.pr_price && selectedProduct.pr_price !== selectedProduct.pr_sprice;


          return (
            <ProductCard key={selectedProduct.pr_id} onClick={() => handleCardClick(selectedProduct)}>
              {selectedProduct.pr_discount && <DiscountLabel>{selectedProduct.pr_discount}% OFF</DiscountLabel>}
              <ImageWrapper>
                <img src={firstImage} alt={selectedProduct.parent_product_name || "Product Image"} />
                <img src={secondImage} alt={selectedProduct.parent_product_name || "Hover Image"} />
              </ImageWrapper>
              <Title>{selectedProduct.parent_product_name}</Title>
              {/* <Description>{selectedProduct.pr_description}</Description> */}
 <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
      {showDiscount ? (
        <>
          <span style={{ color: '#1e81b0' }}>₹{selectedProduct.pr_price}</span>{' '}
          <span style={{ textDecoration: 'line-through', color: '#888' }}>
            ₹{selectedProduct.pr_sprice}
          </span>
        </>
      ) : (
        <span>₹{selectedProduct.pr_sprice}</span>
      )}
    </div>              <ColorOptions>
                {product.relatedProducts?.map((relatedProduct) => {
                  const color = getSimilarProductIconColor(relatedProduct.slug);
                  return (
                    <ColorDot style={{border: '1px solid black'}}
                      key={relatedProduct.pr_id}
                      color={color}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleColorSelect(index, relatedProduct.slug);
                      }}
                    />
                  );
                })}
              </ColorOptions>
            </ProductCard>
          );
        })}
      </Slider>
      <ShowMoreButton onClick={handleShowMoreClick}>Show More Suggestions</ShowMoreButton>
    </Container>
  );
};

export default BestSellers;